import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountStatusesEnum } from 'dash-common';
import { Box, DropDown, Icon, ModalContext, toast } from 'dash-ui';

import {
  OrganizationUser,
  GetOrganizationUsersDocument,
  useSendRegistrationLinkToDeveloperMutation,
} from 'gql/generated.graphql';
import { OrganizationUsersModalsEnum } from 'pages/organizations/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/constants/constants';

const OrganizationUserTableRowActions = ({
  user,
}: {
  user: OrganizationUser;
}) => {
  const { t } = useTranslation(['common', 'tables']);
  const { toggleModal, setPayload } = useContext(ModalContext);
  const [sendRegistrationLinkToUser, { loading }] =
    useSendRegistrationLinkToDeveloperMutation({
      onError: (error) => toast.error(error.toString()),
      onCompleted: () =>
        toast.success(t('common:alerts.invitationHasBeenSent')),
    });

  const sendItemOnClick = () => {
    sendRegistrationLinkToUser({
      variables: { accountId: user.account._id },
      refetchQueries: [GetOrganizationUsersDocument],
    });
  };

  return (
    <Box w={16}>
      <DropDown
        data-testid="row-actions"
        Anchor={() => <Icon name="dots" />}
        items={[
          {
            key: 'edit',
            icon: 'edit',
            w: 170,
            children: t('common:labels.viewEditAction'),
            contentCentered: false,
            onClick: () => {
              toggleModal(
                true,
                OrganizationUsersModalsEnum.SAVE_ORGANIZATION_USER
              );
              setPayload(user);
            },
          },
          {
            key: 'send',
            icon: 'send',
            children: t(
              `tables:organizationUsers.${
                user.account.status === AccountStatusesEnum.ADDED
                  ? 'sendInvitation'
                  : 'resendInvitation'
              }`
            ),
            iconProps: { size: 14 },
            disabled:
              user.account.status === AccountStatusesEnum.ARCHIVED ||
              user.account.status === AccountStatusesEnum.ACTIVE,
            contentCentered: false,
            w: 170,
            onClick: sendItemOnClick,
            isLoading: loading,
          },
          {
            key: 'archive',
            icon: 'archive',
            children: t('common:labels.archive'),
            w: 170,
            disabled: user.account.status !== AccountStatusesEnum.ACTIVE,
            contentCentered: false,
            onClick: () => {
              setPayload({
                status: AccountStatusesEnum.ARCHIVED,
                organizationUserId: user._id,
              });
              toggleModal(
                true,
                OrganizationUsersModalsEnum.SET_ORGANIZATION_USER_STATUS
              );
            },
          },
          {
            key: 'unarchive',
            icon: 'unarchive',
            children: t('common:labels.unarchive'),
            disabled: user.account.status !== AccountStatusesEnum.ARCHIVED,
            contentCentered: false,
            w: 170,
            onClick: () => {
              setPayload({
                status: AccountStatusesEnum.ACTIVE,
                organizationUserId: user._id,
              });
              toggleModal(
                true,
                OrganizationUsersModalsEnum.SET_ORGANIZATION_USER_STATUS
              );
            },
          },
          {
            key: 'close',
            icon: 'close',
            children: t('common:labels.delete'),
            w: 170,
            contentCentered: false,
            onClick: () => {
              setPayload({
                organizationUserId: user._id,
                userName: `${user.profile.firstName} ${user.profile.lastName}`,
              });
              toggleModal(
                true,
                OrganizationUsersModalsEnum.DELETE_ORGANIZATION_USER
              );
            },
          },
        ]}
      />
    </Box>
  );
};

export default OrganizationUserTableRowActions;
